/**
 * Single source of truth for primary site navigation and footer links.
 *
 * Background: P1-03 (footer link dedup) and P1-04 (nav desktop+mobile dedup)
 * from ANTIGRAVITY_REMEDIATION_2026-04-29. Centralising the link config
 * lets us assert "no duplicate hrefs in DOM" via tests and prevents drift
 * between the desktop nav, mobile drawer, and footer columns.
 *
 * Conventions:
 * - All paths are absolute (start with `/`).
 * - Labels follow AU English (no em dashes; en dashes only where typographically
 *   correct).
 */
export type NavItem = {
  href: string;
  label: string;
  /** Optional emphasis flag for CTAs (e.g. submit). */
  emphasis?: boolean;
};

export type FooterColumn = {
  heading: string;
  ariaLabel: string;
  items: ReadonlyArray<NavItem>;
};

/**
 * Primary nav items shared by the desktop nav rail and the mobile drawer.
 * Keep this list ordered so desktop + mobile render the same sequence.
 */
export const PRIMARY_NAV_ITEMS: ReadonlyArray<NavItem> = [
  { href: "/cosmetic-surgery", label: "Cosmetic Surgery" },
  { href: "/orthodontics", label: "Orthodontics" },
  { href: "/ivf-fertility", label: "IVF & Fertility" },
  { href: "/gastroenterology", label: "Gastroenterology" },
  { href: "/ophthalmology", label: "Ophthalmology" },
  { href: "/urology", label: "Urology" },
] as const;

/**
 * Secondary nav items (cross-cutting destinations). Rendered on both
 * breakpoints alongside the primary list.
 */
export const SECONDARY_NAV_ITEMS: ReadonlyArray<NavItem> = [
  { href: "/providers", label: "Providers" },
  { href: "/medicare", label: "Medicare" },
  { href: "/guides", label: "All Procedures" },
  { href: "/insights", label: "Cost Guides" },
] as const;

/**
 * The CTA shown in the nav rail and at the bottom of the mobile drawer.
 */
export const NAV_PRIMARY_CTA: NavItem = {
  href: "/submit",
  label: "Share what you paid",
  emphasis: true,
};

/**
 * Compact desktop rail (xl breakpoint). The full primary list is too wide
 * for the rail, so the desktop visual surface trims to the top three
 * verticals plus the cross-cutting links. Mobile drawer renders the full
 * `PRIMARY_NAV_ITEMS` list. The canonical link target set still matches:
 * any href present on desktop is also reachable via the mobile drawer
 * (no orphaned routes), and we verify this invariant in tests.
 */
export const DESKTOP_PRIMARY_RAIL: ReadonlyArray<NavItem> = [
  { href: "/cosmetic-surgery", label: "Cosmetic" },
  { href: "/orthodontics", label: "Dental" },
  { href: "/ivf-fertility", label: "IVF" },
] as const;

export const DESKTOP_SECONDARY_RAIL: ReadonlyArray<NavItem> = [
  { href: "/providers", label: "Providers" },
  { href: "/guides", label: "All Procedures" },
  { href: "/insights", label: "Cost Guides" },
] as const;

/**
 * Footer columns. Each href appears at most once across all columns;
 * verified by `site-nav.test.ts`. Adding a duplicate href here will
 * fail the test before the DOM ships.
 *
 * Layout spec (slimmed): 3 data columns on desktop: Browse, Resources, Company.
 * Reduced from 4 columns as part of footer-slim audit (Phase C.3).
 */
export const FOOTER_COLUMNS: ReadonlyArray<FooterColumn> = [
  {
    heading: "Browse",
    ariaLabel: "Footer browse procedures and cities",
    items: [
      { href: "/cosmetic-surgery/rhinoplasty-cost", label: "Rhinoplasty" },
      { href: "/orthodontics/invisalign-cost", label: "Invisalign" },
      { href: "/ivf-fertility/ivf-fresh-cycle-cost", label: "IVF Fresh Cycle" },
      { href: "/cosmetic-surgery/sydney", label: "Sydney" },
      { href: "/cosmetic-surgery/melbourne", label: "Melbourne" },
      { href: "/guides", label: "All 107 procedures", emphasis: true },
    ],
  },
  {
    heading: "Resources",
    ariaLabel: "Footer resources and Medicare links",
    items: [
      { href: "/medicare", label: "Medicare Coverage" },
      { href: "/insights", label: "Cost Guides" },
      { href: "/providers", label: "Clinic Directory" },
      { href: "/medicare/is-ivf-fresh-cycle-covered-by-medicare", label: "IVF & Medicare" },
      { href: "/medicare/is-rhinoplasty-covered-by-medicare", label: "Rhinoplasty & Medicare" },
    ],
  },
  {
    heading: "Company",
    ariaLabel: "Footer company and legal links",
    items: [
      { href: "/methodology", label: "Our Methodology" },
      { href: "/about", label: "About" },
      { href: "/faq", label: "FAQ" },
      { href: "/privacy", label: "Privacy Policy" },
      { href: "/terms", label: "Terms of Service" },
      { href: "/contact", label: "Contact" },
    ],
  },
] as const;

/** All hrefs surfaced anywhere in the footer link grid (one per row). */
export function collectFooterHrefs(): string[] {
  return FOOTER_COLUMNS.flatMap((col) => col.items.map((item) => item.href));
}

/** All hrefs surfaced anywhere in the primary nav (desktop or mobile). */
export function collectNavHrefs(): string[] {
  return [
    ...PRIMARY_NAV_ITEMS.map((item) => item.href),
    ...SECONDARY_NAV_ITEMS.map((item) => item.href),
    ...DESKTOP_PRIMARY_RAIL.map((item) => item.href),
    ...DESKTOP_SECONDARY_RAIL.map((item) => item.href),
    NAV_PRIMARY_CTA.href,
  ];
}
